import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Avatar,
  IconButton,
  Backdrop,
  Fade,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Business,
  DriveEta,
  Person,
  Close,
  ArrowForward,
  AddCircle,
} from '@mui/icons-material';
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";

const TopUpHome = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [id, setId] = useState("");
  const navigate = useNavigate();

  const roleOptions = [
    {
      type: "vendor",
      title: "Vendor",
      description: "Top up vendor accounts",
      icon: <Business />,
      color: "#2196F3",
      bgColor: "#E3F2FD",
    },
    {
      type: "driver",
      title: "Driver",
      description: "Top up driver earnings",
      icon: <DriveEta />,
      color: "#4CAF50",
      bgColor: "#E8F5E8",
    },
    {
      type: "customer",
      title: "Customer",
      description: "Top up customer wallets",
      icon: <Person />,
      color: "#FF9800",
      bgColor: "#FFF3E0",
    },
  ];

  const openModal = (type) => {
    setModalType(type);
    setModalOpen(true);
    setId("");
  };

  const handleSubmit = () => {
    if (!id.trim()) {
      return;
    }
    
    setModalOpen(false);
    
    if (modalType === "vendor") {
      navigate(`/vendor/${id}`);
    } else if (modalType === "driver") {
      navigate(`/user/${id}`);
    } else if (modalType === "customer") {
      navigate(`/user/${id}`);
    }
    
    setId("");
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setId("");
  };

  const getRoleDetails = (type) => {
    return roleOptions.find(role => role.type === type) || roleOptions[0];
  };

  return (
    <DashboardLayout>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: 'background.default',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6, maxWidth: 800 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <AccountBalanceWallet sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h3" component="h1" fontWeight="bold" color="primary.main">
              Accounts Top-Up
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Select a user role to manage account top-ups and transactions
          </Typography>
        </Box>

        {/* Role Selection Cards */}
        <Grid container spacing={4} justifyContent="center" sx={{ maxWidth: 1000, mx: 'auto' }}>
          {roleOptions.map((role) => (
            <Grid size={{ xs: 12, md: 4 }} key={role.type}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '2px solid transparent',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                    borderColor: role.color,
                  },
                }}
                onClick={() => openModal(role.type)}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: role.bgColor,
                      color: role.color,
                      width: 64,
                      height: 64,
                      mx: 'auto',
                      mb: 2,
                      fontSize: 32,
                    }}
                  >
                    {role.icon}
                  </Avatar>
                  
                  <Typography variant="h5" component="h2" fontWeight="bold" sx={{ mb: 1 }}>
                    {role.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {role.description}
                  </Typography>
                  
                  <Button
                    variant="contained"
                    startIcon={<AddCircle />}
                    sx={{
                      bgcolor: role.color,
                      '&:hover': {
                        bgcolor: role.color,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Select {role.title}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Options */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/billing')}
            sx={{ px: 4, py: 2 }}
          >
            View All Transactions
          </Button>
        </Box>

        {/* Modal for ID Entry */}
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden'
            }
          }}
        >
          {modalType && (
            <>
              <DialogTitle sx={{ 
                bgcolor: getRoleDetails(modalType).bgColor,
                color: getRoleDetails(modalType).color,
                py: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: getRoleDetails(modalType).color }}>
                      {getRoleDetails(modalType).icon}
                    </Avatar>
                    <Typography variant="h5" component="h2" fontWeight="bold">
                      Enter {getRoleDetails(modalType).title} ID
                    </Typography>
                  </Box>
                  <IconButton onClick={handleCloseModal} sx={{ color: 'inherit' }}>
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              
              <DialogContent sx={{ p: 4 }}>
                <TextField
                  fullWidth
                  label={`${getRoleDetails(modalType).title} ID`}
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  placeholder={`Enter ${getRoleDetails(modalType).title.toLowerCase()} ID`}
                  variant="outlined"
                  size="large"
                  autoFocus
                  sx={{ mb: 2 }}
                  helperText="Enter the unique identifier for the account you want to top up"
                />
                
                <Box sx={{ 
                  p: 2, 
                  bgcolor: getRoleDetails(modalType).bgColor, 
                  borderRadius: 2,
                  border: `1px solid ${getRoleDetails(modalType).color}20`
                }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Example:</strong> For {getRoleDetails(modalType).title.toLowerCase()} accounts, 
                    enter the unique ID number associated with the account.
                  </Typography>
                </Box>
              </DialogContent>
              
              <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Button
                  onClick={handleCloseModal}
                  variant="outlined"
                  sx={{ px: 3 }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={!id.trim()}
                  endIcon={<ArrowForward />}
                  sx={{ 
                    px: 3,
                    bgcolor: getRoleDetails(modalType).color,
                    '&:hover': {
                      bgcolor: getRoleDetails(modalType).color,
                      opacity: 0.9,
                    },
                  }}
                >
                  Continue
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default TopUpHome;
